import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Fade, Grow, InputBase, styled } from "@mui/material";
import { WhatsNextOptionId } from "./WhatsNextOption";
import { PatientColorMap } from "../../../layout/patientColorMap";
import { useTranslate } from "../../../hooks/useTranslate";
import { GenericButton } from "../../uiControllers/genericButton";
import clsx from "clsx";
import { PatientPopup } from "../../uiControllers/patientPopup";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { ThreeDotsLoader } from "fe-web-components/loader/threeDotsLoader";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import { useClinicData } from "../../../clinic/clinicHooks";

//region [[ Styles ]]

const PopupTitle = styled("div")({
  width: "100%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  fontWeight: 700,
  fontSize: "21px",
  lineHeight: "22px",
  color: PatientColorMap.Default,
});

const LocationEntryInput = styled(InputBase)({
  borderRadius: "4px",
  background: PatientColorMap.DefaultLight,
  color: PatientColorMap.Default,
  fontWeight: 500,
  "& input": {
    textAlign: "center",
    padding: "20px",
  },
});

const EmailEntryInput = styled(LocationEntryInput)({
  "& input": {
    textAlign: "left",
    ...onRtl({
      textAlign: "right",
    }),
  },
  margin: "15px 0",
});

const EmailDisclaimer = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "space-between",
  padding: "15px",
  fontSize: "15px",
  lineHeight: "21px",
});

const EmailSubmittedText = styled("div")({
  width: "100%",
  borderRadius: "8px",
  lineHeight: "22px",
  color: PatientColorMap.Green,
  padding: "15px 0",
});

const SubmitButton = styled(GenericButton)({
  "&.generic-button.submit, &.generic-button.submit:hover, &.generic-button.submit:active": {
    "&, &.clicked": {
      width: "25%",
      backgroundColor: PatientColorMap.Primary,
    },
  },
});

const InvalidNotification = styled("div")({
  fontSize: "9px",
  lineHeight: "22px",
  color: "maroon",
  fontWeight: 300,
});

const InputWithError = styled("div")({
  display: "flex",
  flexFlow: "column",
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface DummyLocationBasedProps {
  className?: string;
  optionType: WhatsNextOptionId;
  open?: boolean;
  onClose?: () => void;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
};

const validatePostcode = (code) => {
  return String(code).match(/^([A-Z]*[0-9]*)+$/);
};

export const DummyLocationBased = ({
  open,
  onClose,
  optionType,
  ...props
}: DummyLocationBasedProps) => {
  const [isOpen, setIsOpen] = useState(!!open);

  const [loading, setLoading] = useState(false);

  const [locationEntered, setLocationEntered] = useState(false);

  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const [emailAddress, setEmailAddress] = useState("");

  const [postcode, setPostcode] = useState("");

  const { translate } = useTranslate();

  const handleEmailChanged = useCallback(
    (e) => {
      setEmailAddress(e.target.value);
    },
    [setEmailAddress],
  );

  const handlePostcodeChanged = useCallback(
    (e) => {
      setPostcode(e.target.value);
    },
    [setPostcode],
  );

  //Sync state with props
  useEffect(() => {
    setIsOpen(open);
  }, [open]);

  const { name: clinicName, clinic: clinicId } = useClinicData();

  const handleSubmitClicked = useCallback(() => {
    // Do a fake loading for one seconds
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 2000);
    if (!locationEntered) {
      evt({
        category: CATEGORY.PATIENT_RESULT,
        action: ACTION.PATIENT_WHATS_NEXT,
        label: `${optionType} - entered post code ${postcode}`,
        props: {
          clinicName,
          clinicId,
        },
      });
      setTimeout(() => setLocationEntered(true), 2000);
      return;
    }
    if (!emailSubmitted) {
      evt({
        category: CATEGORY.PATIENT_RESULT,
        action: ACTION.PATIENT_WHATS_NEXT,
        label: `${optionType} - entered email ${emailAddress}`,
        props: {
          clinicName,
          clinicId,
        },
      });
      setEmailSubmitted(true);
      return;
    }
    setIsOpen(false);
  }, [
    setLocationEntered,
    locationEntered,
    emailSubmitted,
    optionType,
    postcode,
    emailAddress,
    clinicName,
    clinicId,
  ]);

  const postcodeValid = useMemo(
    () => postcode && postcode !== "" && validatePostcode(postcode),
    [postcode],
  );

  const emailIsValid = useMemo(
    () => emailAddress && emailAddress !== "" && validateEmail(emailAddress),
    [emailAddress],
  );

  const handleCloseClicked = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose, setIsOpen]);

  return (
    <PatientPopup
      className={clsx(props.className, "location-based-popup-background")}
      open={isOpen}
      onClose={handleCloseClicked}
    >
      <PopupTitle>{translate("ENTER_ZIP_CODE_TITLE")}</PopupTitle>
      <Grow timeout={100} in={!locationEntered} mountOnEnter unmountOnExit>
        <InputWithError>
          <LocationEntryInput
            placeholder={translate("ENTER_ZIPCODE_PLACEHOLDER")}
            onChange={handlePostcodeChanged}
            value={postcode}
          />
          <Fade
            in={postcode !== "" && !postcodeValid}
            mountOnEnter
            unmountOnExit
          >
            <InvalidNotification>
              {translate("ENTER_VALID_ZIPCODE")}
            </InvalidNotification>
          </Fade>
        </InputWithError>
      </Grow>
      <Grow timeout={500} in={loading} mountOnEnter unmountOnExit>
        <ThreeDotsLoader />
      </Grow>
      <Grow timeout={500} in={locationEntered} mountOnEnter unmountOnExit>
        <EmailDisclaimer>
          {translate("NOT_SUPPORTED_LOCATION")}
          <Fade in={!emailSubmitted} mountOnEnter unmountOnExit timeout={100}>
            <InputWithError>
              <EmailEntryInput
                placeholder={translate("ENTER_EMAIL_PLACEHOLDER")}
                onChange={handleEmailChanged}
                value={emailAddress}
              />
              <Fade
                in={emailAddress !== "" && !emailIsValid}
                mountOnEnter
                unmountOnExit
              >
                <InvalidNotification>
                  {translate("ENTER_VALID_EMAIL")}
                </InvalidNotification>
              </Fade>
            </InputWithError>
          </Fade>
          <Fade
            in={!loading && emailSubmitted}
            mountOnEnter
            unmountOnExit
            timeout={500}
          >
            <EmailSubmittedText>
              {translate("THANK_YOU_FOR_YOUR_EMAIL")}
            </EmailSubmittedText>
          </Fade>
        </EmailDisclaimer>
      </Grow>
      <SubmitButton
        className={"submit"}
        label={!emailSubmitted ? translate("SUBMIT") : translate("CLOSE_TEXT")}
        onClick={handleSubmitClicked}
        skipAnalytics
        disabled={
          (!locationEntered && !postcodeValid) ||
          (locationEntered && !emailIsValid)
        }
      />
    </PatientPopup>
  );
};

export const createDummyLocationBased = (optionType: WhatsNextOptionId) => (
  props: DummyLocationBasedProps,
) => <DummyLocationBased optionType={optionType} {...props} />;
